<script setup lang="ts">
import {
  BadgePlus,
  CircleUserRound,
  Dice5,
  House,
  Landmark,
  Share2,
  TicketPercent,
  Trophy,
  WalletCards,
} from '@lucide/vue';
import { computed, defineAsyncComponent, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { RouterLink, RouterView, useRoute, useRouter } from 'vue-router';
import {
  APP_PREVIEW_NODE_SELECT_MESSAGE,
  APP_PREVIEW_READY_MESSAGE,
  isAppPreviewRuntimeMessage,
  type AppPreviewNodeSelectMessage,
} from '@/entities/runtime/preview-bridge';
import { isSessionExpired } from '@/entities/session/session-expiry';
import { ApiBusinessError } from '@/shared/api/result';
import { registerAuthFailureHandler } from '@/shared/api/auth-failure';
import { t } from '@/shared/i18n/messages';
import {
  activeSkin,
  activeSkinModule,
  isGc987Skin,
  isLorUsSkin,
  isOroMxSkin,
} from '@/shared/theme/active-skin';
import { gc987NavAssets } from '@/shared/theme/gc987-h5-skin';
import { lorUsNavAssets } from '@/shared/theme/lor-us-h5-skin';
import { bindLorUsRem } from '@/shared/theme/lor-us-rem';
import { dismissOroMxInitialSplash } from '@/shared/theme/oro-mx-initial-splash';
import { bindOroMxRem } from '@/shared/theme/oro-mx-rem';
import AppToastHost from '@/widgets/app-shell/AppToastHost.vue';
import AuthModal from '@/widgets/app-shell/AuthModal.vue';
import OroMxFirstDeposit from '@/widgets/app-shell/OroMxFirstDeposit.vue';
import OroMxFloats from '@/widgets/app-shell/OroMxFloats.vue';
import OroMxGameSearch from '@/widgets/app-shell/OroMxGameSearch.vue';
import OroMxHeader from '@/widgets/app-shell/OroMxHeader.vue';
import OroMxPushBanner from '@/widgets/app-shell/OroMxPushBanner.vue';
import OroMxScratchModal from '@/widgets/app-shell/OroMxScratchModal.vue';
import OroMxJackpotModal from '@/widgets/app-shell/OroMxJackpotModal.vue';
import OroMxSideMenu from '@/widgets/app-shell/OroMxSideMenu.vue';
import OroMxTabbar from '@/widgets/app-shell/OroMxTabbar.vue';
import { useAuthModalStore } from '@/stores/auth-modal-store';
import { useRuntimeStore } from '@/stores/runtime-store';
import { useSessionStore } from '@/stores/session-store';
import { useWalletStore } from '@/stores/wallet-store';
import { bindOroMxPwaInstallReward } from '@/features/pwa/bind-oro-mx-pwa-install-reward';

let unbindOroMxPwaReward: (() => void) | undefined;
let tryOroMxPwaReward: (() => void) | undefined;

const runtimeStore = useRuntimeStore();
const sessionStore = useSessionStore();
const walletStore = useWalletStore();
const route = useRoute();
const router = useRouter();
const isPreviewShell = new URLSearchParams(window.location.search).get('preview') === 'skin-center';
/**
 * B-lite 单出口：注册了 SkinModule 的皮肤整套壳由模块提供，下面所有 isGc987/isLorUs/isOroMx 分支对它都不生效。
 * 会话恢复 / 鉴权失效 / runtime boot / AuthModal / Toast 仍由本组件统一负责。
 */
const skinShell = activeSkinModule ? defineAsyncComponent(activeSkinModule.shell) : null;
const showGc987PwaBar = ref(isGc987Skin);
// 「我的」页（account/wallet）自带含余额的高级 Hero，隐藏全局顶部 header 避免重复；
// 皮肤预览态保留 header 以便在皮肤中心选中编辑。
const hideHeader = computed(() => {
  if (isPreviewShell) return false;
  // lor-us 首页自带 fixed header；其它页 M0 暂用页面内导航，隐藏全局壳头
  if (isLorUsSkin || isOroMxSkin) return true;
  if (route.name === 'account' || route.name === 'wallet') return true;
  // 现网 /main/promo：仅 ion-segment 顶栏，无 logo/ENTRAR header
  if (isGc987Skin && (route.name === 'promotions' || route.name === 'promotionDetail')) return true;
  // 现网 Depósito：自带 ion-header（标题+记录），隐藏全局 header
  if (isGc987Skin && route.name === 'deposit') return true;
  return false;
});

function dismissGc987PwaBar() {
  showGc987PwaBar.value = false;
}

function dismissOroMxBootSplash() {
  dismissOroMxInitialSplash();
}

const balanceLabel = computed(() => {
  if (!sessionStore.isAuthenticated) return t('account.login');
  return walletStore.loaded ? walletStore.balanceText : '—';
});
const balanceStyle = computed(() => {
  const length = balanceLabel.value.length;
  const compactAmount = Math.max(0, length - 11);
  const fontSize = Math.max(11.2, 15 - compactAmount * 0.5);

  return {
    '--balance-font-size': `${fontSize}px`,
  };
});

type ShellNavItem = {
  center?: boolean;
  icon: typeof House;
  iconSrc?: string;
  iconSrcActive?: string;
  label: string;
  name: string;
  to: string;
};

let unbindLorUsRem: (() => void) | undefined;
let unbindOroMxRem: (() => void) | undefined;
let unbindSkinModuleRem: (() => void) | undefined;
let sessionExpiryTimer = 0;

function promptExpiredSession(notice = t('auth.sessionExpired')) {
  sessionStore.clearSession();
  walletStore.reset();
  useAuthModalStore().openAuth({ mode: 'login', notice });
}

function checkSessionExpiry() {
  if (!sessionStore.token || !isSessionExpired(sessionStore.expiresAt)) return;
  promptExpiredSession();
}

const defaultNavItems = computed<ShellNavItem[]>(() => {
  if (isLorUsSkin) {
    return [
      {
        icon: WalletCards,
        iconSrc: lorUsNavAssets.deposit,
        iconSrcActive: lorUsNavAssets.depositActive,
        label: 'Deposit',
        name: 'deposit',
        to: '/deposit',
      },
      {
        icon: TicketPercent,
        iconSrc: lorUsNavAssets.promotion,
        iconSrcActive: lorUsNavAssets.promotionActive,
        label: 'Promotion',
        name: 'promotions',
        to: '/promotions',
      },
      {
        center: true,
        icon: House,
        iconSrc: lorUsNavAssets.play,
        iconSrcActive: lorUsNavAssets.playActive,
        label: 'PLAY',
        name: 'home',
        to: '/',
      },
      {
        icon: Share2,
        iconSrc: lorUsNavAssets.share,
        iconSrcActive: lorUsNavAssets.shareActive,
        label: 'Share',
        name: 'agent',
        to: '/agent-share',
      },
      {
        icon: BadgePlus,
        iconSrc: lorUsNavAssets.bonus,
        iconSrcActive: lorUsNavAssets.bonusActive,
        label: 'Bonus',
        name: 'bonus',
        to: '/bonus',
      },
    ];
  }
  if (isGc987Skin) {
    return [
      {
        icon: House,
        iconSrc: gc987NavAssets.inicio,
        label: t('nav.home'),
        name: 'home',
        to: '/',
      },
      {
        icon: TicketPercent,
        iconSrc: gc987NavAssets.promo,
        label: t('nav.promo'),
        name: 'promotions',
        to: '/promotions',
      },
      {
        center: true,
        icon: WalletCards,
        iconSrc: gc987NavAssets.depositOn,
        label: t('nav.deposit'),
        name: 'deposit',
        to: '/deposit',
      },
      {
        icon: Landmark,
        iconSrc: gc987NavAssets.withdraw,
        label: t('nav.withdraw'),
        name: 'withdraw',
        to: '/withdraw',
      },
      {
        icon: CircleUserRound,
        iconSrc: gc987NavAssets.perfil,
        label: t('nav.account'),
        name: 'account',
        to: '/account',
      },
    ];
  }
  return [
    { icon: House, label: t('nav.home'), name: 'home', to: '/' },
    { icon: TicketPercent, label: t('nav.activities'), name: 'activities', to: '/activities' },
    { center: true, icon: WalletCards, label: t('nav.deposit'), name: 'deposit', to: '/deposit' },
    { icon: Share2, label: t('nav.agent'), name: 'agent', to: '/agent-share' },
    { icon: CircleUserRound, label: t('nav.account'), name: 'account', to: '/account' },
  ];
});

const navIconMap = {
  account: CircleUserRound,
  activities: TicketPercent,
  agent: Share2,
  bonus: BadgePlus,
  casino: Dice5,
  deposit: WalletCards,
  games: Trophy,
  home: House,
  promotions: TicketPercent,
  promos: TicketPercent,
  sports: Trophy,
  venue: Trophy,
  wallet: WalletCards,
  withdraw: Landmark,
};

const navTargetMap = {
  account: '/account',
  activities: '/activities',
  agent: '/agent-share',
  bonus: '/bonus',
  casino: '/games',
  deposit: '/deposit',
  games: '/games',
  home: '/',
  promotions: '/promotions',
  promos: '/promos/tigrinho-royale',
  sports: '/games',
  venue: '/games',
  wallet: '/wallet',
  withdraw: '/withdraw',
};

const currentPreviewPage = computed(() => {
  const draft = runtimeStore.previewDraft;
  if (!draft) return undefined;
  return (
    draft.pages.find((page) => page.id === draft.selectedPageId) ??
    draft.pages.find((page) => page.route === route.path)
  );
});

const previewAssetsById = computed(() => {
  return new Map(
    runtimeStore.previewDraft?.assets.map((asset) => [asset.id, asset]) ?? [],
  );
});

const previewHeaderComponent = computed(() => {
  return currentPreviewPage.value?.components.find(
    (component) => component.slot === 'header' && component.visible !== false,
  );
});

const previewHeaderComponentId = computed(() => {
  return previewHeaderComponent.value?.id;
});

function previewAssetUrl(assetId: string | undefined, fallback: string) {
  return assetId ? previewAssetsById.value.get(assetId)?.url ?? fallback : fallback;
}

const brandLogoSrc = computed(() =>
  previewAssetUrl(
    previewHeaderComponent.value?.props.logoAssetId,
    activeSkin.assets.logo,
  ),
);
const avatarSrc = computed(() =>
  previewAssetUrl(
    previewHeaderComponent.value?.props.avatarAssetId,
    activeSkin.assets.avatar,
  ),
);
const brandName = computed(
  () => previewHeaderComponent.value?.props.title ?? activeSkin.brandName,
);

const previewNavComponentId = computed(() => {
  return route.name === 'wallet' || route.name === 'account' || route.name === 'kyc'
    ? 'wallet-bottom-nav'
    : 'home-bottom-nav';
});

const previewNavComponent = computed(() => {
  const components = currentPreviewPage.value?.components ?? [];
  return (
    components.find(
      (component) => component.id === previewNavComponentId.value && component.visible !== false,
    ) ??
    components.find((component) => component.slot === 'nav' && component.visible !== false) ??
    runtimeStore.previewDraft?.pages
      .flatMap((page) => page.components)
      .find((component) => component.id === previewNavComponentId.value)
  );
});

const navItems = computed(() => {
  const items = previewNavComponent.value?.props.items?.filter(
    (item) => item.enabled !== false,
  );

  if (!items?.length) return defaultNavItems.value;

  return items.map((item): ShellNavItem => ({
    center: item.id === 'wallet' || item.id === 'deposit',
    icon: navIconMap[item.id as keyof typeof navIconMap] ?? CircleUserRound,
    label: item.label,
    name: item.id,
    to: item.target ?? navTargetMap[item.id as keyof typeof navTargetMap] ?? '/',
  }));
});

/** 源站 /profile 无全局 header；Depositar 全屏钱包盖住 header，但底栏仍在 */
const showOroMxChrome = computed(() => {
  if (!isOroMxSkin) return false;
  const name = String(route.name ?? '');
  return name !== 'account' && name !== 'wallet' && name !== 'deposit' && name !== 'withdraw';
});

const oroMxHideHeaderTabbar = new Set([
  'accountRecord',
  'agentShare',
  'betRecord',
  'gameDetails',
  'signIn',
  'teamCenterHistory',
  'teamCenterInfo',
  'teamCenterShare',
  'notice',
  'profile',
  'profileEdit',
  'rewardDetails',
  'securitySettings',
  'transactionHistory',
  'withdrawAccounts',
]);

const showOroMxHeader = computed(() => {
  if (!showOroMxChrome.value) return false;
  const name = String(route.name ?? '');
  return name !== 'activityHallDetail' && !oroMxHideHeaderTabbar.has(name);
});

const showOroMxTabbar = computed(() => {
  if (!isOroMxSkin) return false;
  const name = String(route.name ?? '');
  if (name === 'account' || name === 'wallet' || name === 'signIn') return false;
  return !oroMxHideHeaderTabbar.has(name);
});

/** 活动浮标 / 首充弹层只挂首页第一屏，不进促销、VIP、游戏列表等子页 */
const showOroMxHomeOverlays = computed(
  () => isOroMxSkin && String(route.name ?? '') === 'home',
);

/** 源站 sortgame 无底栏（uni-tabbar 宽高为 0） */
const showLorUsBottomNav = computed(() => {
  if (isOroMxSkin) return false;
  if (!isLorUsSkin) return true;
  const name = route.name;
  return name !== 'games' && name !== 'venue' && name !== 'venueLobby';
});

const activeNavName = computed(() => {
  if (route.name === 'games' || route.name === 'venue' || route.name === 'venueLobby') return 'venue';
  if (route.name === 'agentShare' || route.name === 'teamCenterHistory' || route.name === 'teamCenterInfo' || route.name === 'teamCenterShare') return 'agent';
  if (route.name === 'wallet' || route.name === 'kyc') return 'wallet';
  if (route.name === 'bonus') return 'bonus';
  if (route.name === 'activities') {
    if (isLorUsSkin) return 'bonus';
    return isGc987Skin ? 'promotions' : 'activities';
  }
  if (route.name === 'promotions' || route.name === 'promotionDetail' || route.name === 'vip' || route.name === 'redeem') {
    if (isLorUsSkin) return 'promotions';
    return isGc987Skin ? 'promotions' : 'activities';
  }
  if (route.name === 'account' || route.name === 'profile' || route.name === 'profileEdit' || route.name === 'withdrawAccounts') return 'account';
  if (route.name === 'deposit') return 'deposit';
  if (route.name === 'withdraw') return 'withdraw';
  return 'home';
});

function lorUsNavIcon(item: ShellNavItem) {
  const active =
    activeNavName.value === item.name ||
    (item.name === 'home' && activeNavName.value === 'home');
  if (active && item.iconSrcActive) return item.iconSrcActive;
  return item.iconSrc;
}

const selectedPreviewComponentId = computed(
  () => runtimeStore.previewDraft?.selectedComponentId,
);
const isPreviewHeaderSelected = computed(() =>
  Boolean(
    isPreviewShell &&
      previewHeaderComponentId.value &&
      selectedPreviewComponentId.value === previewHeaderComponentId.value,
  ),
);
const isPreviewNavSelected = computed(() =>
  Boolean(
    isPreviewShell &&
      previewNavComponentId.value &&
      selectedPreviewComponentId.value === previewNavComponentId.value,
  ),
);

function sendPreviewReady() {
  window.parent?.postMessage({ type: APP_PREVIEW_READY_MESSAGE }, '*');
}

async function applyPreviewMessage(event: MessageEvent) {
  if (!isPreviewShell || !isAppPreviewRuntimeMessage(event.data)) {
    return;
  }

  const { route: nextRoute, runtime, skinDraft, themeTokens } = event.data.payload;
  await runtimeStore.applyPreviewRuntime(runtime, themeTokens, skinDraft);

  if (nextRoute && router.currentRoute.value.path !== nextRoute) {
    await router.replace(nextRoute);
  }
}

function handlePreviewNodeClick(event: MouseEvent) {
  if (!isPreviewShell) {
    return;
  }

  const target = event.target;
  if (!(target instanceof HTMLElement)) {
    return;
  }

  const node = target.closest<HTMLElement>('[data-skin-component-id]');
  if (!node?.dataset.skinComponentId) {
    return;
  }

  const message: AppPreviewNodeSelectMessage = {
    payload: {
      componentId: node.dataset.skinComponentId,
      itemId: node.dataset.skinItemId,
    },
    type: APP_PREVIEW_NODE_SELECT_MESSAGE,
  };

  event.preventDefault();
  event.stopPropagation();
  window.parent?.postMessage(message, '*');
}

onMounted(() => {
  if (activeSkinModule) unbindSkinModuleRem = activeSkinModule.bindRem?.();
  else if (isOroMxSkin) unbindOroMxRem = bindOroMxRem();
  else if (isLorUsSkin) unbindLorUsRem = bindLorUsRem();
  const restoreStatus = sessionStore.restoreSession();
  if (isPreviewShell) {
    dismissOroMxBootSplash();
    window.addEventListener('message', applyPreviewMessage);
    window.addEventListener('click', handlePreviewNodeClick, true);
    sendPreviewReady();
    return;
  }

  // 登录态失效（本地 24h 到期或接口返回 401/鉴权码）时统一清会话并提示重新登录；
  // 游客浏览公开接口报鉴权错误不打扰（无 token 直接忽略，由页面各自降级）。
  registerAuthFailureHandler((error) => {
    if (!sessionStore.token) return;
    promptExpiredSession(error instanceof ApiBusinessError && error.code === 'AUTH_IP_DENIED'
      ? t('auth.serverError.ipDenied') : undefined);
  });
  window.addEventListener('visibilitychange', checkSessionExpiry);
  window.addEventListener('focus', checkSessionExpiry);
  sessionExpiryTimer = window.setInterval(checkSessionExpiry, 30_000);

  if (restoreStatus === 'expired') {
    promptExpiredSession();
  }

  void runtimeStore.boot();
  if (sessionStore.isAuthenticated) {
    void sessionStore.fetchProfile();
    void walletStore.loadAll();
  }
  if (isOroMxSkin) {
    const pwaReward = bindOroMxPwaInstallReward(() => ({
      playerId: sessionStore.player?.playerUserId,
      token: sessionStore.token,
    }));
    unbindOroMxPwaReward = pwaReward.unbind;
    tryOroMxPwaReward = pwaReward.tryClaim;
  }
});

watch(
  () => sessionStore.isAuthenticated,
  (authenticated) => {
    if (authenticated) {
      void walletStore.loadAll();
      tryOroMxPwaReward?.();
    } else walletStore.reset();
  },
);

watch(
  () => runtimeStore.status,
  (status) => {
    if (!isOroMxSkin) return;
    if (status === 'idle' || status === 'booting') return;
    dismissOroMxBootSplash();
  },
);

onBeforeUnmount(() => {
  unbindSkinModuleRem?.();
  unbindSkinModuleRem = undefined;
  unbindLorUsRem?.();
  unbindLorUsRem = undefined;
  unbindOroMxRem?.();
  unbindOroMxRem = undefined;
  unbindOroMxPwaReward?.();
  unbindOroMxPwaReward = undefined;
  tryOroMxPwaReward = undefined;
  window.clearInterval(sessionExpiryTimer);
  sessionExpiryTimer = 0;
  window.removeEventListener('visibilitychange', checkSessionExpiry);
  window.removeEventListener('focus', checkSessionExpiry);
  registerAuthFailureHandler(null);
  if (!isPreviewShell) {
    return;
  }

  window.removeEventListener('message', applyPreviewMessage);
  window.removeEventListener('click', handlePreviewNodeClick, true);
});
</script>

<template>
  <!-- B-lite 出口：SkinModule 皮肤整套壳由模块渲染，slot 内是启动态 / RouterView -->
  <div v-if="skinShell && activeSkinModule" class="app-frame" :class="activeSkinModule.rootClass">
    <component :is="skinShell">
      <section
        v-if="runtimeStore.status === 'booting' || runtimeStore.status === 'idle'"
        class="state-panel state-panel--skin-module-boot"
        aria-busy="true"
      />
      <section
        v-else-if="runtimeStore.status === 'incompatible' || runtimeStore.status === 'error'"
        class="state-panel state-panel--danger"
      >
        <Landmark :size="28" aria-hidden="true" />
        <strong>{{ t('shell.unavailable') }}</strong>
        <span v-for="error in runtimeStore.errors" :key="error">{{ error }}</span>
      </section>
      <RouterView v-else />
    </component>
    <AuthModal />
    <AppToastHost />
  </div>

  <div
    v-else
    class="app-frame"
    :class="{ 'skin-gc987': isGc987Skin, 'skin-lor-us': isLorUsSkin, 'skin-oro-mx': isOroMxSkin }"
  >
    <!-- 现网 #pwa-header-bar：icon | 文案+礼盒 | Instalar | close(text-lg) -->
    <!-- 现网 /main/promo 无 PWA 条，仅首页等展示 -->
    <div
      v-if="isGc987Skin && showGc987PwaBar && route.name !== 'promotions' && route.name !== 'promotionDetail' && route.name !== 'deposit'"
      class="gc987-pwa-bar"
    >
      <img class="gc987-pwa-bar__icon" src="/skins/gc987-h5/brand/icon.png" alt="" />
      <div class="gc987-pwa-bar__mid">
        <p class="gc987-pwa-bar__text">Baixe Nosso APP, Ganhe Super Prêmios!</p>
        <img class="gc987-pwa-bar__prize" src="/skins/gc987-h5/gc987.com/images/pwa-prize.png" alt="" />
      </div>
      <button class="gc987-pwa-bar__install" type="button">Instalar</button>
      <button class="gc987-pwa-bar__close" type="button" aria-label="Fechar" @click="dismissGc987PwaBar">
        <svg class="gc987-pwa-bar__close-icon" viewBox="0 0 512 512" aria-hidden="true">
          <path
            fill="none"
            stroke="currentColor"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="32"
            d="M368 368 144 144m224 0L144 368"
          />
        </svg>
      </button>
    </div>

    <OroMxPushBanner v-if="isOroMxSkin && showOroMxHeader" />
    <OroMxHeader v-if="isOroMxSkin && showOroMxHeader" />

    <header
      v-else-if="!hideHeader"
      class="app-header"
      :class="{
        'app-header--guest': !sessionStore.isAuthenticated,
        'app-header--gc987': isGc987Skin,
      }"
    >
      <!-- 现网 HeaderBar 三槽：menu-btn(start) / logo(pr-4) / primaryBtn+search(end) -->
      <template v-if="isGc987Skin">
        <div class="gc987-header-start">
          <button class="gc987-icon-btn gc987-icon-btn--menu" type="button" :aria-label="t('shell.mainNav')">
            <img :src="gc987NavAssets.menu" alt="" />
          </button>
        </div>
        <RouterLink
          class="brand-logo gc987-header-logo"
          to="/"
          :aria-label="t('shell.backHome')"
          :data-skin-component-id="isPreviewShell ? previewHeaderComponentId : undefined"
          :data-skin-selected="isPreviewHeaderSelected"
        >
          <img :src="brandLogoSrc" :alt="brandName" />
        </RouterLink>
        <div v-if="sessionStore.isAuthenticated" class="gc987-header-end gc987-header-actions">
          <div class="balance-pill" :style="balanceStyle">
            <RouterLink class="balance-pill__amount" to="/account" :aria-label="t('shell.openWallet')">
              <strong>{{ balanceLabel }}</strong>
            </RouterLink>
            <RouterLink class="balance-pill__deposit" to="/deposit" :aria-label="t('deposit.title')">
              <BadgePlus :size="13" aria-hidden="true" />
            </RouterLink>
          </div>
          <RouterLink class="gc987-icon-btn gc987-icon-btn--search" to="/games" :aria-label="t('common.search')">
            <!-- 现网 ion-icon.search-icon 内联 SVG（viewBox 0 0 20 20 / stroke 1.5），勿用 img+invert -->
            <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path
                d="M14.5 14.5L18.5 18.5M16.4545 8.72727C16.4545 12.9949 12.9949 16.4545 8.72727 16.4545C4.45962 16.4545 1 12.9949 1 8.72727C1 4.45962 4.45962 1 8.72727 1C12.9949 1 16.4545 4.45962 16.4545 8.72727Z"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </RouterLink>
        </div>
        <div v-else class="gc987-header-end gc987-header-actions gc987-auth-actions" aria-label="Autenticação">
          <div class="gc987-primary-btns">
            <RouterLink class="gc987-auth-btn gc987-auth-btn--login" to="/login">ENTRAR</RouterLink>
            <RouterLink class="gc987-auth-btn gc987-auth-btn--register" to="/login?mode=register">
              REGISTRO
            </RouterLink>
          </div>
          <RouterLink class="gc987-icon-btn gc987-icon-btn--search" to="/games" :aria-label="t('common.search')">
            <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path
                d="M14.5 14.5L18.5 18.5M16.4545 8.72727C16.4545 12.9949 12.9949 16.4545 8.72727 16.4545C4.45962 16.4545 1 12.9949 1 8.72727C1 4.45962 4.45962 1 8.72727 1C12.9949 1 16.4545 4.45962 16.4545 8.72727Z"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </RouterLink>
        </div>
      </template>

      <template v-else>
        <RouterLink
          class="brand-logo"
          to="/"
          :aria-label="t('shell.backHome')"
          :data-skin-component-id="isPreviewShell ? previewHeaderComponentId : undefined"
          :data-skin-selected="isPreviewHeaderSelected"
        >
          <img :src="brandLogoSrc" :alt="brandName" />
        </RouterLink>
        <button class="country-switch" type="button" :aria-label="t('shell.selectCountry')">
          <span>🇧🇷</span>
          <strong>{{ t('shell.country') }}</strong>
        </button>
        <template v-if="sessionStore.isAuthenticated">
          <div class="balance-pill" :style="balanceStyle">
            <RouterLink class="balance-pill__amount" to="/account" :aria-label="t('shell.openWallet')">
              <strong>{{ balanceLabel }}</strong>
            </RouterLink>
            <RouterLink class="balance-pill__deposit" to="/deposit" :aria-label="t('deposit.title')">
              <BadgePlus :size="13" aria-hidden="true" />
            </RouterLink>
          </div>
          <RouterLink class="avatar-link" to="/account" :aria-label="t('shell.openAccount')">
            <img :src="avatarSrc" alt="" />
            <span class="avatar-age-badge">18+</span>
          </RouterLink>
        </template>

        <div v-else class="auth-actions">
          <RouterLink class="auth-actions__login" to="/login">
            {{ t('account.login') }}
          </RouterLink>
          <RouterLink class="auth-actions__register" to="/login?mode=register">
            {{ t('account.register') }}
          </RouterLink>
        </div>
      </template>
    </header>

    <main
      class="app-main"
      :class="{
        'app-main--flush':
          hideHeader &&
          !(isGc987Skin && (route.name === 'promotions' || route.name === 'promotionDetail' || route.name === 'deposit')),
        'app-main--gc987-home': isGc987Skin && route.name === 'home',
        'app-main--gc987-promo': isGc987Skin && (route.name === 'promotions' || route.name === 'promotionDetail'),
        'app-main--gc987-deposit': isGc987Skin && route.name === 'deposit',
        'app-main--oro-mx': isOroMxSkin,
        'app-main--oro-mx-game': isOroMxSkin && route.name === 'gameDetails',
        'app-main--lor-us-home': isLorUsSkin && route.name === 'home',
        'app-main--lor-us-share': isLorUsSkin && route.name === 'agentShare',
        'app-main--lor-us-games':
          isLorUsSkin &&
          (route.name === 'games' || route.name === 'venue' || route.name === 'venueLobby'),
      }"
    >
      <section
        v-if="runtimeStore.status === 'booting' || runtimeStore.status === 'idle'"
        :class="isOroMxSkin ? 'state-panel state-panel--oro-mx-boot' : 'state-panel'"
      >
        <template v-if="!isOroMxSkin">
          <Landmark :size="28" aria-hidden="true" />
          <strong>{{ t('shell.booting') }}</strong>
          <span>{{ t('shell.bootingDesc') }}</span>
        </template>
      </section>

      <section v-else-if="runtimeStore.status === 'incompatible' || runtimeStore.status === 'error'" class="state-panel state-panel--danger">
        <Landmark :size="28" aria-hidden="true" />
        <strong>{{ t('shell.unavailable') }}</strong>
        <span v-for="error in runtimeStore.errors" :key="error">{{ error }}</span>
      </section>

      <template v-else>
        <RouterView />
      </template>
    </main>

    <OroMxTabbar v-if="isOroMxSkin && showOroMxTabbar" />
    <OroMxSideMenu v-if="isOroMxSkin && showOroMxTabbar" />
    <OroMxFloats v-if="showOroMxHomeOverlays" />
    <OroMxGameSearch v-if="isOroMxSkin" />
    <OroMxFirstDeposit v-if="showOroMxHomeOverlays" />
    <OroMxScratchModal v-if="isOroMxSkin" />
    <OroMxJackpotModal v-if="isOroMxSkin" />

    <nav
      v-if="showLorUsBottomNav"
      class="bottom-nav"
      :class="{ 'bottom-nav--gc987': isGc987Skin, 'bottom-nav--lor-us': isLorUsSkin }"
      :aria-label="t('shell.mainNav')"
      :data-skin-component-id="isPreviewShell ? previewNavComponentId : undefined"
      :data-skin-selected="isPreviewNavSelected"
      :style="
        isGc987Skin
          ? {
              '--gc987-tabbar-bg': `url('${gc987NavAssets.tabbarBg}')`,
              '--gc987-deposit-pedestal': `url('${gc987NavAssets.depositPedestal}')`,
            }
          : undefined
      "
    >
      <RouterLink
        v-for="item in navItems"
        :key="item.name"
        class="bottom-nav__item"
        :class="{
          'bottom-nav__item--active': activeNavName === item.name || (item.name === 'games' && activeNavName === 'venue') || (item.name === 'sports' && activeNavName === 'venue'),
          'bottom-nav__item--center': item.center,
          'bottom-nav__item--lor-play': isLorUsSkin && item.center,
        }"
        :to="item.to"
      >
        <span v-if="!item.center || !isGc987Skin" class="bottom-nav__icon">
          <img
            v-if="item.iconSrc"
            class="bottom-nav__img"
            :class="{ 'bottom-nav__img--lor-play': isLorUsSkin && item.center }"
            :src="isLorUsSkin ? lorUsNavIcon(item) : item.iconSrc"
            alt=""
            aria-hidden="true"
          />
          <component v-else :is="item.icon" :size="item.center ? 28 : 24" aria-hidden="true" />
        </span>
        <span
          :class="{
            'bottom-nav__label': true,
            'bottom-nav__label--center': Boolean(item.center && (isGc987Skin || isLorUsSkin)),
            'bottom-nav__label--lor-play': Boolean(item.center && isLorUsSkin),
          }"
        >
          {{ item.label }}
        </span>
      </RouterLink>

      <!-- 源站：中间 Depósito 图标绝对叠在 notch 上，与文字分离 -->
      <RouterLink
        v-if="isGc987Skin"
        class="bottom-nav__deposit"
        to="/deposit"
        :aria-label="t('nav.deposit')"
        tabindex="-1"
      >
        <img
          class="bottom-nav__deposit-img"
          :src="gc987NavAssets.depositOn"
          alt=""
          aria-hidden="true"
        />
      </RouterLink>
    </nav>

    <AuthModal />
    <AppToastHost />
  </div>
</template>

<style>
.skin-oro-mx {
  font-family: 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', 'Helvetica Neue', sans-serif;
}

html[lang='zh-TW'] .skin-oro-mx {
  font-family: 'PingFang TC', 'Microsoft JhengHei', 'Helvetica Neue', sans-serif;
}

.skin-oro-mx .state-panel--oro-mx-boot {
  min-height: 100%;
  padding: 0;
  background: transparent;
  border: 0;
  box-shadow: none;
}

/*
 * B-lite 皮肤启动态：壳已由模块渲染（header / 底栏 / 底图），内容区留空即可。
 * main.ts 先 import App 再 import styles.css，全局 .state-panel（深色面板 + 12px 圆角 + 220px）
 * 级联在本样式之后；这里必须提高特异度，否则 Rosa 刷新会先出一块黑色圆角占位。
 */
.app-frame .state-panel.state-panel--skin-module-boot {
  min-height: 100%;
  padding: 0;
  background: transparent;
  border: 0;
  border-radius: 0;
  box-shadow: none;
}
</style>
